import { NotifyHelper } from 'classes/helpers/notify.helper';
import env from 'config';
import { IUserRoleFeatureFlags } from 'interfaces/i-user-feature-flags';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { PitchListsRoutes } from 'lib_ts/enums/routing/routes/pitch-lists';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import {
  ICopyPitchLists,
  IPitch,
  IPitchList,
  IPitchListPutManyRequest,
  IPitchListSummaryDict,
  IRenameFolderRequest,
} from 'lib_ts/interfaces/pitches';
import { uniq } from 'lodash';
import { BaseRESTService } from 'services/_base-rest.service';

export class PitchListsService extends BaseRESTService {
  private static instance: PitchListsService;
  static getInstance(): PitchListsService {
    if (!PitchListsService.instance) {
      PitchListsService.instance = new PitchListsService();
    }

    return PitchListsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.PitchLists,
    });
  }

  /** if env.enable_team_lists = true => list respects editor selection, otherwise list will be owned by user */
  async postList(data: Partial<IPitchList>): Promise<IPitchList | undefined> {
    return await this.post(
      {
        uri: PitchListsRoutes.Create,
      },
      data
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          throw new Error(result.error);
        }

        return result.data as IPitchList;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  }

  /** creates a copy using data's id to select existing list for duplication,
   * but with the copied list's name as data's name */
  async copyOne(
    data: WithID<Partial<IPitchList>>
  ): Promise<IPitchList | undefined> {
    return await this.post(
      {
        uri: PitchListsRoutes.CopyOne,
        params: { list_id: data._id } as any,
      },
      data
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          return undefined;
        }

        return result.data as IPitchList;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  }

  async getOne(listID: string): Promise<IPitchList | undefined> {
    if (!listID) {
      throw new Error('Cannot get without id');
    }

    return await this.get({
      uri: PitchListsRoutes.GetOne,
      params: {
        list_id: listID,
      } as any,
    }).then((result: IServerResponse) => {
      if (!result.success) {
        return undefined;
      }

      return result.data as IPitchList;
    });
  }

  // returns a list of bs_hashes that were modified
  async restore(listID: string): Promise<string[]> {
    if (!listID) {
      throw new Error('Cannot get without id');
    }

    return await this.get({
      uri: PitchListsRoutes.Restore,
      params: {
        list_id: listID,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (result.success) {
          const pitchIDs = result.data as string[];

          if (pitchIDs.length === 0) {
            NotifyHelper.info({
              message_md:
                'Pitch list restore completed. No pitches were changed.',
            });
          } else {
            NotifyHelper.success({
              message_md: `Pitch list restore completed. ${pitchIDs.length} pitch(es) changed.`,
            });
          }

          return pitchIDs;
        }

        console.error(result.error);

        NotifyHelper.warning({
          message_md: result.error ?? 'Pitch list restore failed.',
        });

        return result.data ?? [];
      })
      .catch((error) => {
        console.error(error);

        NotifyHelper.error({
          message_md: 'Pitch list restore failed.',
        });

        return [];
      });
  }

  async updateOne(data: Partial<IPitchList>): Promise<IServerResponse> {
    if (!data._id) {
      throw new Error('Cannot put without id');
    }

    return await this.put(
      {
        uri: PitchListsRoutes.UpdateOne,
        params: {
          list_id: data._id,
        } as any,
      },
      data
    );
  }

  async renameFolder(config: IRenameFolderRequest): Promise<IServerResponse> {
    return await this.post(
      {
        uri: PitchListsRoutes.RenameFolder,
      },
      config
    );
  }

  // because the insertion uses insertMany chunked, the server response is just a boolean of whether it all succeeded instead of the inserted records
  async addPitches(config: {
    listID: string;
    data: Partial<IPitch>[];
  }): Promise<IServerResponse> {
    /** cleanup data before submission */
    config.data.forEach((p) => {
      /** ensure new pitches are created, e.g. when copying/saving from existing */
      delete p._id;
    });

    return await this.post(
      {
        uri: PitchListsRoutes.AddPitches,
        params: {
          list_id: config.listID,
        } as any,
      },
      config.data
    );
  }

  async getVisible(flags: IUserRoleFeatureFlags): Promise<IPitchList[]> {
    const reference_types: ReferenceListType[] = [];

    if (flags.manage.pitchListType) {
      reference_types.push(...Object.values(ReferenceListType));
    }

    if (env.enable.sample_lists) {
      reference_types.push(ReferenceListType.Sample);
    }

    return await this.post(
      {
        uri: PitchListsRoutes.GetVisible,
      },
      {
        reference_types:
          reference_types.length > 0 ? uniq(reference_types) : undefined,
      }
    );
  }

  async getLiveReferences(): Promise<IPitchList[]> {
    return await this.get({
      uri: PitchListsRoutes.GetLiveReferences,
    });
  }

  async updateMany(
    payload: IPitchListPutManyRequest
  ): Promise<IServerResponse> {
    return await this.put(
      {
        uri: PitchListsRoutes.UpdateMany,
      },
      payload
    );
  }

  async copyMany(payload: ICopyPitchLists): Promise<IServerResponse> {
    return await this.post(
      {
        uri: PitchListsRoutes.CopyMany,
      },
      payload
    );
  }

  async deleteMany(listIDs: string[]): Promise<IServerResponse> {
    return await this.post(
      {
        uri: PitchListsRoutes.DeleteMany,
      },
      listIDs
    );
  }

  /** import CSV to bulk update pitches in the list with limited subset of provided data (e.g. pitch title, type, video_id) */
  async importCsv(
    listID: string,
    files: File[]
  ): Promise<{ event: string; success: boolean; error?: any }> {
    /** append the files */
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));

    return await this.post(
      {
        uri: PitchListsRoutes.ImportCsv,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          list_id: listID,
        } as any,
      },
      formData
    );
  }

  async getHashSummaries(): Promise<IPitchListSummaryDict> {
    return await this.get({
      uri: PitchListsRoutes.GetHashSummaries,
    })
      .then((res: IServerResponse) => {
        if (res.success) {
          return res.data;
        }

        NotifyHelper.warning({
          message_md:
            res.error ??
            'There was a problem fetching pitch list hash summaries.',
        });

        return {};
      })
      .catch((e) => {
        console.error(e);

        NotifyHelper.error({
          message_md: 'There was a problem fetching pitch list hash summaries.',
        });

        return {};
      });
  }
}
